import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Car audio system / stereo install",
  "Subwoofer build & enclosure",
  "Amplifier install & tuning",
  "LED / interior lighting",
  "Wraps & window tint",
  "Rims & accessories",
  "Backup camera / tech install",
  "Home audio & video automation",
  "Something else",
];

const empty = { name: "", phone: "", email: "", vehicle: "", service: SERVICE_OPTIONS[0], message: "" };

export default function QuoteSection() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quote`, form);
      toast.success("Quote request sent — we'll call you back shortly.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Call us at (302) 268-6466.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/15 bg-[#0A0B0E] px-4 py-3.5 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#FF5500]";

  return (
    <section id="quote" className="relative py-24 sm:py-36 bg-[#0D0F14] border-t border-white/5 grain" data-testid="quote-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <Eyebrow>Ready To Upgrade?</Eyebrow>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[0.98]">
                Let's build something that
                <span className="text-[#FF5500]"> turns heads</span> — and sounds even
                better.
              </h2>
            </Reveal>

            <div className="mt-12 flex flex-col gap-4">
              <Reveal delay={0.1}>
                <a
                  data-testid="quote-call-button"
                  href="tel:3022686466"
                  className="group flex items-center justify-between rounded-2xl border border-white/10 bg-[#12141B] p-6 transition-colors duration-300 hover:border-[#FF5500]/60"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FF5500] volt-glow">
                      <Phone size={18} />
                    </span>
                    <div>
                      <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
                        Call Now
                      </p>
                      <p className="font-display text-xl font-bold">(302) 268-6466</p>
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-white/30 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#FF5500]" />
                </a>
              </Reveal>
              <Reveal delay={0.2}>
                <a
                  data-testid="quote-directions-link"
                  href="https://maps.google.com/?q=867+S+Dupont+Hwy+Unit+2,+New+Castle,+DE+19720"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between rounded-2xl border border-white/10 bg-[#12141B] p-6 transition-colors duration-300 hover:border-[#FF5500]/60"
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#FF5500]/50 text-[#FF5500]">
                      <MapPin size={18} />
                    </span>
                    <div>
                      <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-white/40">
                        The Shop
                      </p>
                      <p className="text-sm font-medium text-white/90">
                        867 S Dupont Hwy, Unit 2, New Castle, DE 19720
                      </p>
                    </div>
                  </div>
                  <ArrowRight size={18} className="text-white/30 transition-all duration-300 group-hover:translate-x-1 group-hover:text-[#FF5500]" />
                </a>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.15}>
            <form
              data-testid="quote-form"
              onSubmit={submit}
              className="rounded-3xl border border-white/10 bg-[#12141B] p-7 sm:p-9"
            >
              <p className="font-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5500] mb-6">
                Get A Quote
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="quote-name-input"
                  required
                  placeholder="Your name *"
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                />
                <input
                  data-testid="quote-phone-input"
                  required
                  placeholder="Phone *"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  className={inputCls}
                />
                <input
                  data-testid="quote-email-input"
                  placeholder="Email"
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls}
                />
                <input
                  data-testid="quote-vehicle-input"
                  placeholder="Vehicle (year / make / model)"
                  value={form.vehicle}
                  onChange={set("vehicle")}
                  className={inputCls}
                />
                <select
                  data-testid="quote-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className={`${inputCls} sm:col-span-2 appearance-none`}
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o} value={o} className="bg-[#12141B]">
                      {o}
                    </option>
                  ))}
                </select>
                <textarea
                  data-testid="quote-message-input"
                  placeholder="Tell us about your build — what are you dreaming up?"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className={`${inputCls} sm:col-span-2 resize-none`}
                />
              </div>
              <button
                data-testid="quote-submit-button"
                type="submit"
                disabled={sending}
                className="mt-6 flex w-full items-center justify-center gap-3 rounded-full bg-[#FF5500] py-4 font-tech text-sm font-bold uppercase tracking-widest text-white transition-all duration-300 hover:scale-[1.02] volt-glow disabled:opacity-60 disabled:hover:scale-100"
              >
                {sending ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                {sending ? "Sending..." : "Send Quote Request"}
              </button>
              <p className="mt-4 text-center text-xs text-white/35">
                Prefer to talk? Call (302) 268-6466 — we answer.
              </p>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
